import { BaseController } from '@maia/feature-utils';

import {
  ActivityDto,
  ActivityService,
  ClaimDto,
  ClaimService,
  InsuranceDocumentDto,
  InsuranceDto,
  InsuranceService,
  InvoiceDto,
  InvoiceService,
  NoteDto,
  NoteService,
  ProductListDto,
  QuotationDto,
  QuotationService,
  TaskDto,
  TaskService,
} from '@maia/shared';

export class InsuranceController extends BaseController {
  constructor(
    private readonly claimService: ClaimService,
    private readonly insuranceService: InsuranceService,
    private readonly invoiceService: InvoiceService,
    private readonly quotationService: QuotationService,
    private readonly activityService: ActivityService,
    private readonly notesService: NoteService,
    private readonly tasksService: TaskService,
  ) {
    super();
  }

  async getInsurances(search: Record<string, unknown>) {
    const response = await this.getEntities(
      this.insuranceService,
      { path: 'insurances/', search },
      InsuranceDto,
    );
    return response;
  }

  async getInsurance(guid: string) {
    const response = await this.getEntity(
      this.insuranceService,
      `insurances/${guid}`,
      InsuranceDto,
    );
    return response;
  }

  async getInsuranceDocuments(
    params: Parameters<typeof this.insuranceService.getDocuments>[0],
  ) {
    const response = await this.insuranceService.getDocuments(params);
    return new InsuranceDocumentDto(response);
  }

  async getClaims(guid: string, search: Record<string, unknown>) {
    return this.getEntities(
      this.claimService,
      { path: `insurances/${guid}/claims`, search },
      ClaimDto,
    );
  }

  async getInvoices(guid: string, search: Record<string, unknown>) {
    return this.getEntities(
      this.invoiceService,
      { path: `insurances/${guid}/invoices`, search },
      InvoiceDto,
    );
  }

  async getQuotations(guid: string, search: Record<string, unknown>) {
    return this.getEntities(
      this.quotationService,
      { path: `insurances/${guid}/quotations`, search },
      QuotationDto,
    );
  }

  async getActivities(guid: string, search: Record<string, unknown>) {
    return this.getEntities(
      this.activityService,
      { path: `insurances/${guid}/activities`, search },
      ActivityDto,
    );
  }

  async getNotes(guid: string, search: Record<string, unknown>) {
    return this.getEntities(
      this.notesService,
      { path: `insurances/${guid}/notes`, search },
      NoteDto,
    );
  }

  async getTasks(guid: string, search: Record<string, unknown>) {
    return this.getEntities(
      this.tasksService,
      { path: `insurances/${guid}/tasks`, search },
      TaskDto,
    );
  }

  async createNote(
    path: Parameters<typeof this.notesService.create>[0],
    params: Parameters<typeof this.notesService.create>[1],
  ) {
    return await this.notesService.create(path, params);
  }

  async getFilterProducts() {
    const result = await this.insuranceService.getFilterProducts();
    return new ProductListDto(result);
  }
}
